import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import clsx from 'clsx';
import path from 'path';

import { Icon } from '../Icon/Icon';
import styles from './DropZone.module.scss';
import { useFakeProgress } from 'hooks';
import upload_icon from 'assets/images/upload_icon.png';
import { color } from 'html2canvas/dist/types/css/types/color';
import deleteIcon from 'assets/images/bin.png';

import { InputError } from '../InputError/InputError';

export type DropZoneProps = {
  className?: string;
  onUpload?: (files: File[]) => void;
  onRemove?: (index: number) => void;
  accept?: string;
  maxSize?: number;
  maxFileCount?: number;
  selectedFiles?: File[];
  filenames?: string[];
  showEye?: boolean;
  placeholder?: string;
  placeholder2?: string;
  placeholder3?: string;
  className2?: string;
};

export const DropZone: React.FC<DropZoneProps> = ({
  className,
  className2,
  onUpload,
  accept,
  placeholder,
  placeholder2,
  placeholder3,
  maxFileCount = 1,
  onRemove,
  ...args
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [error, setError] = useState('');
  const { progress, start, done, clean } = useFakeProgress();

  /* Sync external selectedFiles */
  useEffect(() => {
    if (args.selectedFiles?.length) {
      setSelectedFiles(args.selectedFiles);
    }
  }, [args.selectedFiles]);

  /* Upload handler */
  const upload = useCallback(
    (files: File[]) => {
      if (onUpload) {
        onUpload(files);
      }
      done();
    },
    [done, onUpload]
  );

  /* Drop handler */
  const onDrop = useCallback(
    (files: File[]) => {
      if (!files.length) return;

      setError('');
      setSelectedFiles(prev => {
        const countLeft = maxFileCount - prev.length;
        const allowedFiles = files.slice(0, countLeft);
        upload(allowedFiles);
        return [...prev, ...allowedFiles];
      });

      start();
    },
    [upload, start, maxFileCount]
  );

  /* Reject handler */
  const onDropRejected = useCallback(
    (files: any[]) => {
      clean();

      if (!files.length) return;

      const maxMb = args.maxSize
        ? args.maxSize / (1024 * 1024)
        : 5;

      if (files[0]?.errors[0]?.code === 'file-invalid-type') {
        setError(`Only image files are allowed.`);
      } else {
        setError(
          `Please upload a file less than 20 MB`
        );
      }
    },
    [clean, args.maxSize]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected,
    accept,
    multiple: maxFileCount > 1,
    maxSize: args.maxSize || 5000000
  });

  /* Remove file */
  const onFileRemove = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    if (onRemove) {
      onRemove(index);
    }
  };

  /* Open image */
  const onEyeClick = (file: File) => {
    const url = URL.createObjectURL(file);
    window.open(url, '_blank');
  };

  return (
    <div className={clsx(styles.root, className)}>
      {/* IMAGE PREVIEW */}
      {selectedFiles.map((file, i) => (
        <UploadItem
          key={i}
          file={file}
          percent={progress}
          onReset={() => onFileRemove(i)}
          onEyeClick={() => onEyeClick(file)}
          showEye={args.showEye}
        />
      ))}

      {/* UPLOAD ZONE */}
      {selectedFiles.length < maxFileCount && (
        <div {...getRootProps()}>
          <input {...getInputProps()} />
          <UploadZone
            className={clsx(className, error && 'border-danger')}
            placeholder={placeholder}
            placeholder2={placeholder2}
            placeholder3={placeholder3}
            className2={className2}
          />
        </div>
      )}

      {error && <ErrorBlock text={error} className="dropzone-error" />}
    </div>
  );
};

/* ================= Upload Zone ================= */

const UploadZone: React.FC<any> = ({
  className,
  placeholder,
  placeholder2,
  placeholder3,
  className2
}) => (
  <div className={clsx(styles.upload, className)}>
    <div className="upload_icons_container">
      <img src={upload_icon} alt="upload" />
    </div>

    <div className="gov_upload_hide">
      {placeholder}
    </div>

    <div className={styles.browse}>
      <span className={`gov_upload_hide ${className2}`}>
        {placeholder2}
      </span>
      <span className={`gov_upload_hide ${className}`}>
        {placeholder3}
      </span>
    </div>
  </div>
);

/* ================= Error ================= */

const ErrorBlock: React.FC<{ text: string; className?: string }> = ({
  text,
  className
}) => (
  <InputError className={className}>
    <span dangerouslySetInnerHTML={{ __html: text }} />
  </InputError>
);

/* ================= Upload Item (IMAGE PREVIEW) ================= */

type UploadItemProps = {
  file: File;
  percent: number;
  onReset: () => void;
  showEye?: boolean;
  onEyeClick?: () => void;
};

const UploadItem: React.FC<UploadItemProps> = ({
  file,
  percent,
  onReset,
  onEyeClick,
  showEye
}) => {
  const isImage = file.type.startsWith('image/');
  const previewUrl = URL.createObjectURL(file);

  return (
    <div className={clsx(styles['upload-item'], 'mb-2')}>
      {isImage ? (
        <img
          src={previewUrl}
          alt={file.name}
          className={styles.imagePreview}
        />
      ) : (
        <div className={styles.fileName}>
          {path.basename(file.name)}
        </div>
      )}

      {percent > 0 && (
        <>
          <div className={styles['progress-value']}>{percent}%</div>
          <div
            className={styles.progress}
            style={{ width: `${percent}%` }}
          />
        </>
      )}

      {percent === 0 && (
        <div className={styles.icons}>
          <img
            src={deleteIcon}
            alt="Delete"
            className={styles.deleteIcon}
            onClick={onReset}
          />
          {showEye && <Icon name="eye" onClick={onEyeClick} />}
        </div>
      )}
    </div>
  );
};
